import { ApiRoutes } from "../../../Constants/ApiRoutingConstants";
import axios, { AxiosInstance } from "axios";
import authorizeService from "../../Identity/Utilities/AuthorizeService";
import { ReservoirReadDto } from "../../../Models/Reservoirs/ReservoirReadDto";
import { AlertReadTableDto } from "../../../Models/Alerts/AlertReadTableDto";
import { AlertCreateDto } from "../../../Models/Alerts/AlertCreateDto";
import { AlertUpdateDto } from "../../../Models/Alerts/AlertUpdateDto";
import { AlertReadDto } from "../../../Models/Alerts/AlertReadDto";
import EndpointWithRequirementReadDto from "../../../Models/Endpoint/EndpointWithRequirementReadDto";
import PressureEndpointsAlertsDto from "../../../Models/Endpoint/PressureEndpointsAlertsDto";
import { AlertsSettingsCron } from "./CronConverter";


/**
 * This service provides implementations for http calls to the api pertaining to alerts crud functionality
 */

class AlertsApiService {
  axiosInstance: AxiosInstance;
  constructor() {
    //Subscribe to identity store the get token
    this.axiosInstance = axios.create({
      baseURL: process.env.REACT_APP_ASSET_SERVICE_URL,
    });
  }

  async getAllAlerts(): Promise<AlertReadTableDto[]> {
    let response = await this.axiosInstance({
      method: "GET",
      url: `${ApiRoutes.Alerts}`,
      headers: {
        Authorization: `Bearer ${(await authorizeService.getUser()).access_token}`,
      },
    });
    return response.data;
  }

  async getAlert(alertId: string | null): Promise<AlertReadDto> {
    let response = await this.axiosInstance({
      method: "GET",
      url: `${ApiRoutes.Alerts}/${alertId}`,
      headers: {
        Authorization: `Bearer ${(await authorizeService.getUser()).access_token}`,
      },
    });
    return response.data;
  }

  async createAlert(alert: AlertCreateDto) {
    let response = await this.axiosInstance({
      method: "POST",
      url: `${ApiRoutes.Alerts}`,
      headers: {
        Authorization: `Bearer ${(await authorizeService.getUser()).access_token}`,
      },
      data: alert,
    });
    return response.data as ReservoirReadDto;
  }

  async updateAlert(alert: AlertUpdateDto, alertId: string) {
    let response = await this.axiosInstance({
      method: "PUT",
      url: `${ApiRoutes.Alerts}/${alertId}`,
      headers: {
        Authorization: `Bearer ${(await authorizeService.getUser()).access_token}`,
      },
      data: alert,
    });
    return response.data;
  }

  async deleteAlert(alertId: string) {
    let response = await this.axiosInstance({
      method: "DELETE",
      url: `${ApiRoutes.Alerts}/${alertId}`,
      headers: {
        Authorization: `Bearer ${(await authorizeService.getUser()).access_token}`,
      }
    });
    return response.data;
  }

  async resetAlert(alert: AlertUpdateDto, alertId: string) {
    let response = await this.axiosInstance({
      method: "PUT",
      url: `${ApiRoutes.Alerts}/${alertId}/reset`,
      headers: {
        Authorization: `Bearer ${(await authorizeService.getUser()).access_token}`,
      },
      data: alert,
    });
    return response.data;
  }

  async getAllEndpointsWithRequirements(): Promise<EndpointWithRequirementReadDto[]> {
    let response = await this.axiosInstance({
      method: "GET",
      url: `${ApiRoutes.EndpointsV1}/tree-data?activeRequirementsOnly=true`,
      headers: {
        Authorization: `Bearer ${(await authorizeService.getUser()).access_token}`,
      },
    });
    return response.data;
  }

  async getAllPressureEndpoints(): Promise<PressureEndpointsAlertsDto[]> {
    let response = await this.axiosInstance({
      method: "GET",
      url: `${ApiRoutes.EndpointsV1}/pressure/tree-data`,
      headers: {
        Authorization: `Bearer ${(await authorizeService.getUser()).access_token}`,
      },
    });
    return response.data;
  }

  async getAllCommsDevices(): Promise<any[]> {
    let response = await this.axiosInstance({
      method: "GET",
      url: `${ApiRoutes.DevicesV1}/tree-data`,
      headers: {
        Authorization: `Bearer ${(await authorizeService.getUser()).access_token}`,
      },
    });
    return response.data;
  }

  async createTestEmail(alertData: any) {
    const response = await this.axiosInstance({
      method: "POST",
      url: `${ApiRoutes.Alerts}/send-test-email`,
      headers: {
        Authorization: `Bearer ${(await authorizeService.getUser()).access_token}`,
      },
      data: alertData
    });
    return response;
  }

  async getAlertsSettings(): Promise<AlertsSettingsCron | null> {
    try {
      const response = await this.axiosInstance({
        method: "GET",
        url: `${ApiRoutes.Notifications}/settings`,
        headers: {
          Authorization: `Bearer ${(await authorizeService.getUser()).access_token}`,
        },
      });
      return response.data;
    } catch (error: any) {
      // Return null if 404 (no settings exist yet)
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  async saveAlertsSettings(settings: AlertsSettingsCron, isNew: boolean): Promise<AlertsSettingsCron> {
    const method = isNew ? "POST" : "PUT";
    const response = await this.axiosInstance({
      method: method,
      url: `${ApiRoutes.Notifications}/settings`,
      headers: {
        Authorization: `Bearer ${(await authorizeService.getUser()).access_token}`,
      },
      data: settings,
    });
    return response.data;
  }

  static get instance() {
    return AlertsApiService;
  }
}

const alertsApiService = new AlertsApiService();
export default alertsApiService;
